import { Button } from '@mui/material'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { openSignModal } from 'src/components/Account/utils'
import BaseModelListInfiniteScroll from 'src/components/GenerationModel/GenerationModelList'
import ModelGrid from 'src/components/GenerationModel/GenerationModelList/ModelGrid'
import type { GenerationModelFeedResult } from 'src/components/GenerationModel/GenerationModelList/useGenerationModelFeed'
import { useGenerationModelFeed } from 'src/components/GenerationModel/GenerationModelList/useGenerationModelFeed'
import ModelFilter from 'src/components/GenerationModel/ModelFilter'
import {
  modelFiltersToModelType,
  modelTypeToModelFilters,
} from 'src/components/GenerationModel/ModelFilter.helper'
import { NavToTrainLoraPageButton } from 'src/components/GrowthUnit/TrainLoraEvent'
import { UITooltipHelpIcon } from 'src/components/HelpIcon'
import ModelSearchForm from 'src/components/Market/ModelSearchForm'
import MetaTags from 'src/components/MetaTags'
import OrderSelect from 'src/components/OrderSelect'
import { Preloader } from 'src/components/Preloader/Preloader'
import MarkdownRenderer from 'src/components/ui/MarkdownRenderer'
import { useMyInfo } from 'src/services/apollo/auth'
import type {
  GenerationModelType,
  ListGenerationModelsQueryVariables,
} from 'src/services/apollo/generated/graphql'
import { OrderOption } from 'src/services/helpers/orderOptionParams'
import { useRefreshCount } from 'src/stores/signal'
import { createTimeRange } from 'src/utils/dayjs'
import { useIsMobile } from 'src/utils/hooks/useBreakpoint'
import { useQs } from 'src/utils/qs'
import { useHash } from 'src/utils/router/useHash'
import { track } from 'src/utils/tracker'

const qsKeys = [
  'order',
  'type',
  'base',
  'category',
  'timeRange',
  'baseModelTypes',
  'keyword',
] as const

export const useModelFilterState = () => {
  const [
    {
      order: queryOrder,
      type: queryType,
      base: queryBase,
      category: queryCategory,
      timeRange: queryTimeRange,
      baseModelTypes: queryBaseModelTypes,
      keyword: queryKeyword,
    },
  ] = useQs()
  const [hash, setHash] = useHash()
  const hashParams = new URLSearchParams(hash.replace(/^#/, ''))

  const queryValues: Record<(typeof qsKeys)[number], string | undefined> = {
    order: queryOrder,
    type: queryType,
    base: queryBase,
    category: queryCategory,
    timeRange: queryTimeRange,
    baseModelTypes: queryBaseModelTypes,
    keyword: queryKeyword,
  }

  // Migrate from query param to hash param
  useEffect(() => {
    const hasQueryParams = qsKeys.some(key => queryValues[key])
    if (!hasQueryParams) return

    const url = new URL(window.location.href)
    for (const key of qsKeys) {
      url.searchParams.delete(key)
    }

    // Preserve existing hash params, only update filter-related ones
    const params = new URLSearchParams(url.hash.replace(/^#/, ''))
    for (const key of qsKeys) {
      if (queryValues[key] === undefined) continue
      if (queryValues[key]) {
        params.set(key, queryValues[key])
      } else {
        params.delete(key)
      }
    }
    url.hash = params.toString()
    window.history.replaceState(null, '', url.toString())

    setHash(params.toString())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queryOrder,
    queryType,
    queryBase,
    queryCategory,
    queryTimeRange,
    queryBaseModelTypes,
    queryKeyword,
    setHash,
  ])

  const defaults: Partial<Record<(typeof qsKeys)[number], string>> = {
    order: OrderOption.trending,
  }

  const filterState = Object.fromEntries(
    qsKeys.map(key => [
      key,
      queryValues[key] || hashParams.get(key) || defaults[key],
    ]),
  ) as Record<(typeof qsKeys)[number], string | undefined>

  const setValue = (vals: {
    order?: string
    type?: string[] | ''
    base?: string // baseModelId
    category?: string
    timeRange?: string
    baseModelTypes?: string[] | ''
    keyword?: string | ''
  }) => {
    const params = new URLSearchParams(hash.replace(/^#/, ''))

    for (const key of qsKeys) {
      if (!(key in vals)) continue
      const value = vals[key]
      if (value && (Array.isArray(value) ? value.length > 0 : true)) {
        params.set(key, String(value))
      } else {
        params.delete(key)
      }
    }
    setHash(params.toString())
  }

  return [filterState, setValue] as const
}

export { MarketPage as Component }
function MarketPage({
  initialData,
}: {
  initialData?: Pick<GenerationModelFeedResult, 'models'>
}) {
  const { tag } = useParams()
  const [filter, setFilter] = useModelFilterState()
  const keyword = filter.keyword

  const { t } = useTranslation(['common', 'market', 'artwork'])

  const navigate = useNavigate()
  const { data: me } = useMyInfo()

  const refreshTime = useRefreshCount()

  const key = useMemo(() => {
    const v = {
      keyword,
      filter,
      refreshTime,
      tag,
    }
    return JSON.stringify(v)
  }, [filter, refreshTime, tag, keyword])

  const orderChoices = [
    {
      value: OrderOption.trending,
      label: t('common:order-option.trending'),
    },
    {
      value: OrderOption.mostLiked,
      label: t('common:order-option.most-liked'),
    },
    {
      value: OrderOption.mostUsed,
      label: t('common:order-option.most-used'),
    },
    {
      value: OrderOption.newest,
      label: t('common:order-option.newest'),
    },
  ]

  const isMobile = useIsMobile()

  return (
    <div className="w-full flex flex-col items-center justify-center md:pt-6">
      <MetaTags
        title={t('common:meta.title.market')}
        description={t('common:meta.description.market')}
        ogUrlPathname="/market"
      />
      <div className="w-full max-w-full flex flex-col items-center">
        <div className="w-full flex flex-col items-center md:gap-2">
          {!isMobile && (
            <div className="self-stretch mt-4">
              <ModelSearchForm keyword={keyword} />
            </div>
          )}

          <div className="mt-4 mb-6 flex gap-2 items-center">
            <NavToTrainLoraPageButton from="market" />
            <Button
              variant="contained"
              className="bg-purple-600 text-white font-bold rounded-lg px-6"
              onClick={() => {
                track('upload_lora_model_click')
                if (me) {
                  navigate('/market/submit')
                } else {
                  openSignModal(
                    {
                      onSuccess() {
                        navigate('/market/submit')
                      },
                    },
                    { fromPath: 'upload-model' },
                  )
                }
              }}
            >
              {t('artwork:upload-local-image')}
            </Button>
            <UITooltipHelpIcon>
              <MarkdownRenderer className="p-4 prose-sm prose prose-neutral dark:prose-invert w-80">
                {t('market:hero.submit.help-text')}
              </MarkdownRenderer>
            </UITooltipHelpIcon>
          </div>
        </div>

        <div className="mt-2 flex max-msm:flex-wrap items-center gap-x-2 gap-y-4 w-full">
          <OrderSelect
            choices={orderChoices}
            onChange={v => {
              track('order_click', {
                value: v,
                scene: 'market',
              })
              setFilter({ order: v })
            }}
            value={filter.order}
            alwaysCollapse
            className="flex-1 msm:flex-none"
          />
          <ModelFilter
            value={{
              ...modelTypeToModelFilters(
                filter.type?.split(','),
                filter.baseModelTypes?.split(','),
              ),

              ...(filter.base ? { baseModelId: filter.base } : {}),
              category: filter.category,
              time: filter.timeRange,
            }}
            onChange={value => {
              track('filter_change', value)
              const filters = modelFiltersToModelType(value)
              setFilter({
                type: filters.type,
                baseModelTypes: filters.loraBaseModelTypes,
                base: /^\d+$/.test(value.baseModelId ?? '')
                  ? value.baseModelId
                  : undefined,

                category: value.category,
                timeRange: value.time,
              })
            }}
            hideDisabled
            className="flex-1 msm:flex-none"
          />
        </div>

        {/*
          Previously wrapped in `<Suspense fallback={<Preloader />}>`.
          With `useSuspense: false` in i18next + no
          `useSuspenseQuery` / `React.lazy` / `dynamic({ ssr: false })`
          in the model-grid render path, nothing throws server-side;
          the boundary only served to ship the grid HTML inside a
          `<div hidden id="S:N">` swap target that JS-disabled
          crawlers and NoScript users never reveal. Removing it lets
          the model grid (`<img>` tags, model titles, author chips)
          inline directly into `<main>` on SSR.
        */}
        <MarketModelGrid
          key={key}
          order={filter.order}
          keyword={keyword}
          tag={tag}
          type={filter.type}
          timeRange={createTimeRange(filter.timeRange ?? '')}
          extraArgs={{
            loraBaseModelIds:
              filter.base && /^\d+$/.test(filter.base)
                ? [filter.base]
                : undefined,
            category: filter.category,
            loraBaseModelTypes: filter.baseModelTypes?.split(
              ',',
            ) as GenerationModelType[],
          }}
          initialData={initialData}
        />
      </div>
    </div>
  )
}

function MarketModelGrid({
  order,
  keyword,
  tag,
  type,
  timeRange,
  extraArgs,
  initialData,
}: {
  order?: string
  keyword?: string
  tag?: string
  type?: string
  timeRange?: ReturnType<typeof createTimeRange>
  extraArgs?: ListGenerationModelsQueryVariables
  initialData?: Pick<GenerationModelFeedResult, 'models'>
}) {
  const fetchedFeed = useGenerationModelFeed({
    order,
    keyword,
    tag,
    type,
    timeRange,
    extraArgs,
  })

  // See the matching comment in `ArtworkListPage`'s `feedResult`
  // useMemo: `<BaseModelListInfiniteScroll>` decides between
  // `renderLoading()` (skeleton) and `renderContent()` (real
  // cards) purely on `feedResult.loading`. Override it to
  // `false` when we adopt `initialData`, otherwise the SSR
  // first-paint emits skeletons even though we already have
  // loader-seeded real `models` to render — which manifests as
  // `/zh/market` shipping a `<main>` with zero `<img>` tags
  // (the imgs live inside the real card branch).
  const feedResult = useMemo<GenerationModelFeedResult>(
    () =>
      initialData && fetchedFeed.loading && fetchedFeed.models.length === 0
        ? { ...fetchedFeed, models: initialData.models, loading: false }
        : fetchedFeed,
    [initialData, fetchedFeed],
  )

  return (
    <BaseModelListInfiniteScroll
      feedResult={feedResult}
      renderContent={innerProps => <ModelGrid scene="market" {...innerProps} />}
      renderLoading={() => <ModelGrid loading />}
    />
  )
}
